import { useEffect, useState } from 'react';

import {
  buildMockContext,
  getMockState,
  initMockScenario,
  MOCK_SCENARIO_PARAM,
  MOCK_SCENARIOS,
  subscribeMockState,
} from '../../mocks';
import type { TDailyMarkingContext } from '../../types';

type TUseDailyMarkingMockReturn = {
  data?: TDailyMarkingContext;
  loading: boolean;
  error: boolean;
  refetch: () => void;
};

/**
 * Returns mock data when `?mock_scenario=<key>` is in the URL.
 * Returns `undefined` when no mock param is present (real hook should be used).
 *
 * The mock starts with all marks PENDING and builds context dynamically.
 * When the mutation mock registers a mark, this hook re-renders with updated marks.
 * Browser refresh resets to all pending — always starts clean.
 *
 * Add `&mutation_mock=<key>` to force a mutation error response.
 *
 * ---
 * ## Available scenarios
 *
 * ### Non-Rotating
 * - {@link http://localhost:3030/org/lambda/attendance/tracker/personal/?mock_scenario=nr_with_break | With break — full 4-mark flow (Entry → Break Start → Break End → Exit)}
 * - {@link http://localhost:3030/org/lambda/attendance/tracker/personal/?mock_scenario=nr_no_break | Without break — 2-mark flow (Entry → Exit)}
 *
 * ### Rotating
 * - {@link http://localhost:3030/org/lambda/attendance/tracker/personal/?mock_scenario=rotating_with_break | With break — full 4-mark flow}
 * - {@link http://localhost:3030/org/lambda/attendance/tracker/personal/?mock_scenario=rotating_no_break | Without break — 2-mark flow}
 *
 * ### Special
 * - {@link http://localhost:3030/org/lambda/attendance/tracker/personal/?mock_scenario=no_shift | No shift assigned — 4 marks, all get REGISTERED status}
 * - {@link http://localhost:3030/org/lambda/attendance/tracker/personal/?mock_scenario=rest_day | Rest day — is_working_day=false, marks get REGISTERED status}
 *
 * ### Mutation errors (combine with any scenario above)
 * - {@link http://localhost:3030/org/lambda/attendance/tracker/personal/?mock_scenario=nr_with_break&mutation_mock=mutation_already_registered | Duplicate mark error}
 * - {@link http://localhost:3030/org/lambda/attendance/tracker/personal/?mock_scenario=nr_with_break&mutation_mock=mutation_no_active_entry | No active entry error}
 * - {@link http://localhost:3030/org/lambda/attendance/tracker/personal/?mock_scenario=nr_with_break&mutation_mock=mutation_network_error | Network error}
 * - {@link http://localhost:3030/org/lambda/attendance/tracker/personal/?mock_scenario=nr_with_break&mutation_mock=mutation_generic_error | Internal server error}
 */
export const useDailyMarkingMock = ():
  | TUseDailyMarkingMockReturn
  | undefined => {
  const search = typeof window === 'undefined' ? '' : window.location.search;
  const scenarioKey =
    new URLSearchParams(search).get(MOCK_SCENARIO_PARAM) ?? '';

  // Counter to force re-renders when the mutation mock updates marks
  const [, setRenderCount] = useState(0);

  // Initialize the scenario once on first render (or after browser refresh)
  const [initialized] = useState(() => {
    if (!scenarioKey || !MOCK_SCENARIOS[scenarioKey]) {
      return false;
    }

    initMockScenario(scenarioKey);

    return true;
  });

  // Subscribe to mark changes from the mutation mock
  useEffect(() => {
    if (!initialized) {
      return;
    }

    return subscribeMockState(() => {
      setRenderCount(prev => prev + 1);
    });
  }, [initialized]);

  // No mock param or invalid scenario → use real hook
  if (!initialized) {
    return undefined;
  }

  const state = getMockState();

  if (!state) {
    // biome-ignore lint/suspicious/noEmptyBlockStatements: mock
    return { data: undefined, loading: false, error: true, refetch: () => {} };
  }

  return {
    data: buildMockContext(state),
    loading: false,
    error: false,
    refetch: () => {
      // Re-render with current state (marks persist, same as Apollo cache-and-network)
      setRenderCount(prev => prev + 1);
    },
  };
};
